import React, { useEffect, useLayoutEffect } from 'react';
import { defineMessages } from 'react-intl';
import type { IntlShape } from 'react-intl';
import type { Transaction } from 'prosemirror-state';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { Node } from '@atlaskit/editor-prosemirror/model';
import type { ExtractInjectionAPI, EditorCommand } from '@atlaskit/editor-common/types';

import { pluginKey, pluginName } from '../../../createPlugin.ts';
// eslint-disable-next-line import/no-cycle
import { checkIfValidAnchorName } from '../anchorMacroToolbarOverride.tsx';
import type { ExtensionManagerPlugin } from '../../../createPlugin.ts';

const i18n = defineMessages({
	defaultTooltipContent: {
		id: 'fabric-extension-manager-plugin.anchor.copy-link-button.tooltip',
		description: 'Text to be shown as a tooltip for the copy link button in the anchor macro',
		defaultMessage: 'Copy link to anchor',
	},
	erroredTooltipContent: {
		id: 'fabric-extension-manager-plugin.anchor.copy-link-button.tooltip.disabled',
		defaultMessage: 'Edit to create link',
		description:
			'Tooltip text for when the copy link button is disabled because the anchor name is missing',
	},
	copySuccessTooltipContent: {
		id: 'fabric-extension-manager-plugin.anchor.copy-link-button.tooltip.copied',
		description: 'Tooltip text for when the link has been copied to the clipboard',
		defaultMessage: 'Copied!',
	},
});

type CopyLinkToolbarItemTooltipContentProps = {
	node: Node;
	intl: IntlShape;
	api?: ExtractInjectionAPI<ExtensionManagerPlugin>;
	updateTooltipPosition?: () => void;
};

export const resetShowAnchorLinkCopiedState =
	(): EditorCommand =>
	({ tr }: { tr: Transaction }) =>
		tr.setMeta(pluginKey, {
			showAnchorLinkCopiedTooltip: undefined,
		});

export const CopyLinkToolbarItemTooltipContent = ({
	node,
	intl,
	api,
	updateTooltipPosition,
}: CopyLinkToolbarItemTooltipContentProps) => {
	const showAnchorLinkCopiedTooltip = useSharedPluginStateWithSelector(
		api,
		[pluginName],
		(states) => states?.extensionManagerState?.showAnchorLinkCopiedTooltip,
	);
	const isValidAnchorName = checkIfValidAnchorName(node);

	useEffect(() => {
		// Reset the state of `showAnchorLinkCopiedTooltip` back to default when the component unmounts
		return () => {
			api?.core?.actions?.execute?.(resetShowAnchorLinkCopiedState());
		};
	}, [api]);

	useLayoutEffect(() => {
		// When the content of a visible tooltip changes, its' position isn't recalculated
		// and the tooltip may become misaligned. Since we change the tooltip content when
		// the link is copied, we need to manually recalculate the tooltip position.
		if (showAnchorLinkCopiedTooltip) {
			updateTooltipPosition?.();
		}
	}, [showAnchorLinkCopiedTooltip, updateTooltipPosition]);

	if (!isValidAnchorName) {
		// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
		return <p>{intl.formatMessage(i18n.erroredTooltipContent)}</p>;
	}

	return (
		// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
		<p>
			{!!showAnchorLinkCopiedTooltip
				? intl.formatMessage(i18n.copySuccessTooltipContent)
				: intl.formatMessage(i18n.defaultTooltipContent)}
		</p>
	);
};
